import React, { useEffect, useState } from 'react';
import { IonContent, IonPage } from '@ionic/react';
import { useHistory, useParams } from 'react-router-dom';
import commentIcon from '../imgs/quotes.png';
import { ApiService } from '../api/ApiService';
import LoadDetailHeader, { useTransparentHeaderContrast } from '../components/LoadDetailHeader';
import { toastStore } from '../components/AppToast';
import { useLogPageVisit } from '../utils/logPageVisit';

interface LoadInfo {
  approved: string;
  blNumber: string;
  comments: string;
  consigneeAddr1: string;
  consigneeAddr2: string;
  consigneeCity: string;
  consigneeCode: string;
  consigneeName: string;
  consigneeNameOrExt: string;
  consigneePhone: string;
  consigneeState: string;
  consigneeZip: string;
  deliveryEndTime: string;
  deliveryStartTime: string;
  pickupNumber: string;
  poNumber: string;
  deliveryNumber: string;
  dispatchNumber: string;
  driver1: string;
  driver2: string;
  driverLoad: boolean | string;
  driverUnload: boolean | string;
  loadNumber: string;
  milesEmpty: number;
  milesLoaded: number;
  milesTotal: number;
  numberOfStops: string;
  orderNumber: string;
  pickupEndTime: string;
  pickupStartTime: string;
  preloadTrailer?: string;
  shipperAddr1: string;
  shipperAddr2: string;
  shipperCity: string;
  shipperCode: string;
  shipperName: string;
  shipperPhone: string;
  shipperState: string;
  shipperZip: string;
  trailer: string;
  tarpPay: boolean;
  orderStatus: string;
}

function placeLine(city?: string, state?: string): string {
  return [city, state].filter(Boolean).join(', ') || '—';
}

function formatMiles(value?: number | null): string {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return Number(value).toLocaleString();
}

const LoadDetail: React.FC = () => {
  useLogPageVisit('LP Load Detail', 'User viewed the Load Detail page');
  const history = useHistory();
  const { onSheet, onIonScroll } = useTransparentHeaderContrast();
  const { loadnum } = useParams<{ loadnum: string }>();
  const [loadInfo, setLoadInfo] = useState<LoadInfo>();
  const [stopInfo, setStopInfo] = useState<any[]>([]);
  const [hasStops, setHasStops] = useState(false);
  const [hasTarpPay, setHasTarpPay] = useState(false);
  const [receivedPhotos, setReceivedPhotos] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => {
      setTimeout(() => setMounted(true), 30);
    });
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    ApiService.getLoadDetails('loadDetails', { orderNumber: loadnum }).then(loadDetails => {
      processLoadInfo(loadDetails);
      processStopInfo(loadDetails);
    })
    .catch(() => {
      toastStore.getState().setToast('There was an error loading the load details. Please check your connection and try again.');
    });
  }, [loadnum]);

  function processLoadInfo(load: any) {
    const ld = load.load;
    ld.comments = Array.isArray(load.load.comments) ? load.load.comments.join('\n') : load.load.comments;
    switch (ld.driverLoad) {
      case 'N':
        ld.driverLoad = 'Live Load';
        break;
      case 'W':
        ld.driverLoad = 'Pre loaded - pickup by end of window';
        break;
      case 'P':
        ld.driverLoad = 'Pre loaded';
        break;
      default:
        break;
    }
    switch (ld.driverUnload) {
      case 'N':
        ld.driverUnload = 'Live Unload';
        break;
      case 'W':
        ld.driverUnload = 'Drop off - deliver by end of window';
        break;
      case 'D':
        ld.driverUnload = 'Drop off';
        break;
      default:
        break;
    }

    setLoadInfo(ld);
    if (ld.tarpPay) {
      checkTarpPhotos(ld.orderNumber);
      setHasTarpPay(true);
    }
  }

  function processStopInfo(stops: any) {
    const st = stops.stops;
    if (!st || st.length < 3) return;
    const tempArray: any[] = [];
    st.forEach((element: any) => {
      if (element.sequenceNumber !== 1 && element.sequenceNumber !== 90) {
        tempArray.push(element);
      }
    });
    setHasStops(true);
    setStopInfo(tempArray);
  }

  const formatComment = (comment: string) => {
    if (comment) return comment;
    return 'No Comment';
  };

  function checkTarpPhotos(orderNum: number) {
    ApiService.getPhotoList('loadDetails', { load: orderNum, type: 'tarp' }).then(it => {
      setReceivedPhotos(it?.photos?.length !== 0);
    });
  }

  const goBack = () => {
    if (history.length > 1) history.goBack();
    else history.replace('/loadhistory');
  };

  const showTrailerWarning =
    !(parseInt(loadInfo?.dispatchNumber || '0') >= 1 && loadInfo?.orderStatus === 'D');
  const origin = placeLine(loadInfo?.shipperCity, loadInfo?.shipperState);
  const destination = placeLine(loadInfo?.consigneeCity, loadInfo?.consigneeState);
  const loadUnload =
    loadInfo?.driverLoad || loadInfo?.driverUnload
      ? `${loadInfo?.driverLoad || '—'} / ${loadInfo?.driverUnload || '—'}`
      : '—';

  return (
    <IonPage>
      <LoadDetailHeader title="Load Detail" onBack={goBack} onSheet={onSheet} />

      <IonContent
        fullscreen
        className="design-a-content"
        scrollEvents={true}
        onIonScroll={onIonScroll}
      >
        <div className={`design-a design-a--detail${mounted ? ' design-a--mounted' : ''}`}>
          <div className="home-hero load-detail-hero">
            <div className="load-detail-hero__eyebrow">Load #{loadInfo?.loadNumber || '—'}</div>
            <div className="load-detail-hero__route">
              <span className="load-detail-hero__origin">{origin}</span>
              <span className="load-detail-hero__arrow" aria-hidden="true">
                →
              </span>
              <span className="load-detail-hero__destination home-display">{destination}</span>
            </div>
            <div className="load-distance load-detail-hero__distance">
              <div className="load-distance__label">Load details</div>
              <div className="load-distance__grid">
                <div className="load-distance__row">
                  <div className="load-distance__col load-distance__col--first">
                    <div className="load-distance__value">{formatMiles(loadInfo?.milesTotal)}</div>
                    <div className="load-distance__unit">total miles</div>
                  </div>
                  <div className="load-distance__col">
                    <div className="load-distance__value">{formatMiles(loadInfo?.milesEmpty)}</div>
                    <div className="load-distance__unit">empty miles</div>
                  </div>
                </div>
                <div className="load-distance__col load-distance__col--eta">
                  <div className="load-distance__value">{formatMiles(loadInfo?.milesLoaded)}</div>
                  <div className="load-distance__unit">loaded miles</div>
                </div>
                <div className="load-distance__col load-distance__col--eta">
                  <div className="load-distance__value load-detail-hero__distance-value--sm">{loadUnload}</div>
                  <div className="load-distance__unit">pickup &amp; delivery</div>
                </div>
              </div>
            </div>
            {showTrailerWarning ? (
              <div className="load-detail-hero__alerts">
                <div className="load-detail-hero__alert load-detail-hero__alert--warn">
                  Bring a trailer to shipper
                </div>
              </div>
            ) : null}
          </div>

          <div className="home-sheet load-detail-sheet">
            <div className="load-detail-card load-detail-card--meta">
              <div className="load-detail-meta">
                <div className="load-detail-meta__row">
                  <span className="load-detail-meta__label">Trailer</span>
                  <span className="load-detail-meta__value">{loadInfo?.trailer || '—'}</span>
                </div>
                <div className="load-detail-meta__row">
                  <span className="load-detail-meta__label">Dispatch</span>
                  <span className="load-detail-meta__value">{loadInfo?.dispatchNumber || '—'}</span>
                </div>
                <div className="load-detail-meta__row">
                  <span className="load-detail-meta__label">Status</span>
                  <span className="load-detail-meta__value">{loadInfo?.orderStatus || '—'}</span>
                </div>
                <div className="load-detail-meta__row">
                  <span className="load-detail-meta__label">Stops</span>
                  <span className="load-detail-meta__value">{loadInfo?.numberOfStops || '—'}</span>
                </div>
                {loadInfo?.preloadTrailer ? (
                  <div className="load-detail-meta__row">
                    <span className="load-detail-meta__label">Preloaded</span>
                    <span className="load-detail-meta__value">{loadInfo.preloadTrailer}</span>
                  </div>
                ) : null}
                <div className="load-detail-meta__row">
                  <span className="load-detail-meta__label">Tarp photos</span>
                  <span className="load-detail-meta__value">
                    {hasTarpPay ? (receivedPhotos ? 'Yes' : 'No') : 'Not Required'}
                  </span>
                </div>
              </div>
            </div>

            <div className="load-detail-card">
              <div className="load-detail-card__header">
                <div className="load-detail-badge load-detail-badge--load">L</div>
                <div className="load-detail-card__title">Load At</div>
              </div>
              <div className="load-detail-card__window">
                {loadInfo?.pickupStartTime} – {loadInfo?.pickupEndTime}
              </div>
              <div className="load-detail-card__name home-display">{loadInfo?.shipperName || '—'}</div>
              <div className="load-detail-card__address">
                {loadInfo?.shipperAddr1 ? <div>{loadInfo.shipperAddr1}</div> : null}
                {loadInfo?.shipperAddr2 ? <div>{loadInfo.shipperAddr2}</div> : null}
                <div>
                  {loadInfo?.shipperCity}, {loadInfo?.shipperState} {loadInfo?.shipperZip}
                </div>
                {loadInfo?.shipperPhone ? <div>{loadInfo.shipperPhone}</div> : null}
              </div>
              <div className="load-detail-grid">
                <div className="load-detail-grid__cell">
                  <span className="load-detail-grid__label">PO</span>
                  <span className="load-detail-grid__value">{loadInfo?.poNumber || '—'}</span>
                </div>
                <div className="load-detail-grid__cell">
                  <span className="load-detail-grid__label">Pickup</span>
                  <span className="load-detail-grid__value">{loadInfo?.pickupNumber || '—'}</span>
                </div>
                <div className="load-detail-grid__cell">
                  <span className="load-detail-grid__label">Order</span>
                  <span className="load-detail-grid__value">{loadInfo?.orderNumber || '—'}</span>
                </div>
                <div className="load-detail-grid__cell">
                  <span className="load-detail-grid__label">BOL</span>
                  <span className="load-detail-grid__value">{loadInfo?.blNumber || '—'}</span>
                </div>
              </div>
            </div>

            {hasStops
              ? stopInfo.map((element: any, index: number) => (
                  <div className="load-detail-card" key={`stop-${element.sequenceNumber || index}`}>
                    <div className="load-detail-card__header">
                      <div className="load-detail-badge load-detail-badge--stop">S</div>
                      <div className="load-detail-card__title">Stop At</div>
                    </div>
                    <div className="load-detail-card__name home-display">
                      {element?.customerName || '—'}
                    </div>
                    <div className="load-detail-card__address">
                      {element?.address1 ? <div>{element.address1}</div> : null}
                      {element?.address2 ? <div>{element.address2}</div> : null}
                      <div>
                        {element?.city}, {element?.state} {element?.zipCode}
                      </div>
                    </div>
                  </div>
                ))
              : null}

            <div className="load-detail-card">
              <div className="load-detail-card__header">
                <div className="load-detail-badge load-detail-badge--consignee">C</div>
                <div className="load-detail-card__title">Consignee</div>
              </div>
              <div className="load-detail-card__window">
                {loadInfo?.deliveryStartTime} – {loadInfo?.deliveryEndTime}
              </div>
              <div className="load-detail-card__name home-display">
                {loadInfo?.consigneeName || '—'} {loadInfo?.consigneeNameOrExt || ''}
              </div>
              <div className="load-detail-card__address">
                {loadInfo?.consigneeAddr1 ? <div>{loadInfo.consigneeAddr1}</div> : null}
                {loadInfo?.consigneeAddr2 ? <div>{loadInfo.consigneeAddr2}</div> : null}
                <div>
                  {loadInfo?.consigneeCity}, {loadInfo?.consigneeState} {loadInfo?.consigneeZip}
                </div>
                {loadInfo?.consigneePhone ? <div>{loadInfo.consigneePhone}</div> : null}
              </div>
              <div className="load-detail-grid">
                <div className="load-detail-grid__cell load-detail-grid__cell--span2">
                  <span className="load-detail-grid__label">Delivery</span>
                  <span className="load-detail-grid__value">{loadInfo?.deliveryNumber || '—'}</span>
                </div>
              </div>
            </div>

            <div className="load-detail-card">
              <div className="load-detail-card__header">
                <div className="load-detail-badge load-detail-badge--comments">
                  <img src={commentIcon} alt="" className="load-detail-badge__img" />
                </div>
                <div className="load-detail-card__title">Comments</div>
              </div>
              <p className="load-detail-comments">{formatComment(loadInfo?.comments ?? '')}</p>
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default LoadDetail;
